/**
 * Custom Variant Dropdown Component
 * Provides a two-column dropdown (swatch + name) with collection grouping support
 */

if (!customElements.get('variant-dropdown')) {
  class VariantDropdown extends HTMLElement {
    constructor() {
      super();
      this.trigger = this.querySelector('.variant-dropdown__trigger');
      this.triggerContainer = this.querySelector('.variant-dropdown__trigger-container');
      this.panel = this.querySelector('.variant-dropdown__panel');
      this.nativeSelect = this.querySelector('.variant-dropdown__native-select');
      this.options = this.querySelectorAll('.variant-dropdown__option');
      this.triggerText = this.querySelector('.variant-dropdown__trigger-text');
      this.triggerSubtitle = this.querySelector('.variant-dropdown__trigger-subtitle');
      this.triggerTextContainer = this.querySelector('.variant-dropdown__trigger-text-container');
      this.triggerSwatchWrapper = this.querySelector('.variant-dropdown__trigger-swatch-wrapper');
      this.triggerSwatchInput = this.querySelector('.variant-dropdown__trigger-swatch-input');
      
      this.isOpen = false;
      this.focusedIndex = -1;
    }

    connectedCallback() {
      this.setupEventListeners();
      this.updateTriggerDisplay();
    }

    setupEventListeners() {
      // Toggle dropdown on trigger button click
      this.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });

      // Toggle dropdown on swatch wrapper click (but not when hovering for tooltip)
      if (this.triggerSwatchWrapper) {
        this.triggerSwatchWrapper.addEventListener('click', (e) => {
          // Don't toggle if clicking on the tooltip itself
          if (e.target.closest('.swatch-tooltip')) return;
          e.preventDefault();
          this.toggle();
        });
      }

      // Handle option selection
      this.options.forEach((option, index) => {
        option.addEventListener('click', () => {
          if (option.getAttribute('aria-disabled') !== 'true') {
            this.selectOption(option);
          }
        });

        option.addEventListener('keydown', (e) => {
          this.handleOptionKeydown(e, index);
        });
      });

      // Keyboard navigation on trigger
      this.trigger.addEventListener('keydown', (e) => {
        this.handleTriggerKeydown(e);
      });

      // Close on click outside
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) {
          this.close();
        }
      });

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
          this.trigger.focus();
        }
      });
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.isOpen = true;
      this.setAttribute('open', '');
      this.trigger.setAttribute('aria-expanded', 'true');
      
      // Focus the selected option or first option
      const selectedOption = this.querySelector('.variant-dropdown__option[aria-selected="true"]');
      if (selectedOption) {
        this.focusedIndex = Array.from(this.options).indexOf(selectedOption);
        selectedOption.focus();
      } else if (this.options.length > 0) {
        this.focusedIndex = 0;
        this.options[0].focus();
      }
    }

    close() {
      this.isOpen = false;
      this.removeAttribute('open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.focusedIndex = -1;
    }

    selectOption(option) {
      const value = option.dataset.value;
      const optionValueId = option.dataset.optionValueId;
      const productUrl = option.dataset.productUrl;
      const swatchValue = option.dataset.swatchValue;
      const swatchFocalPoint = option.dataset.swatchFocalPoint;
      const subtitle = option.dataset.subtitle;

      // Update aria-selected states
      this.options.forEach(opt => opt.setAttribute('aria-selected', 'false'));
      option.setAttribute('aria-selected', 'true');

      // Update native select value
      if (this.nativeSelect) {
        this.nativeSelect.value = value;
        
        // Update native select option attributes
        Array.from(this.nativeSelect.options).forEach(opt => {
          opt.removeAttribute('selected');
          if (opt.value === value) {
            opt.setAttribute('selected', 'selected');
          }
        });

        // Dispatch change event for variant selection system
        this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Update trigger display
      this.updateTriggerDisplay(value, swatchValue, swatchFocalPoint, subtitle);

      // Close dropdown
      this.close();
      this.trigger.focus();
    }

    updateTriggerDisplay(value, swatchValue, swatchFocalPoint, subtitle) {
      // If no value provided, get from currently selected option
      if (!value) {
        const selectedOption = this.querySelector('.variant-dropdown__option[aria-selected="true"]');
        if (selectedOption) {
          value = selectedOption.dataset.value;
          swatchValue = selectedOption.dataset.swatchValue;
          swatchFocalPoint = selectedOption.dataset.swatchFocalPoint;
          subtitle = selectedOption.dataset.subtitle;
        }
      }

      // Update text
      if (this.triggerText && value) {
        this.triggerText.textContent = value;
      }

      // Update subtitle
      if (this.triggerTextContainer) {
        if (subtitle) {
          // Create subtitle element if it doesn't exist
          if (!this.triggerSubtitle) {
            this.triggerSubtitle = document.createElement('span');
            this.triggerSubtitle.className = 'variant-dropdown__trigger-subtitle';
            this.triggerSubtitle.setAttribute('data-selected-subtitle', '');
            this.triggerTextContainer.appendChild(this.triggerSubtitle);
          }
          this.triggerSubtitle.textContent = subtitle;
          this.triggerSubtitle.style.display = '';
        } else if (this.triggerSubtitle) {
          // Hide subtitle if no subtitle for this option
          this.triggerSubtitle.style.display = 'none';
        }
      }

      // Update hidden input value (for tooltip JS compatibility)
      if (this.triggerSwatchInput && value) {
        this.triggerSwatchInput.value = value;
      }

      // Update subtitle data attribute on swatch wrapper (for tooltip)
      if (this.triggerSwatchWrapper) {
        if (subtitle) {
          this.triggerSwatchWrapper.dataset.subtitle = subtitle;
        } else {
          delete this.triggerSwatchWrapper.dataset.subtitle;
        }
      }

      // Update swatch in the wrapper
      if (this.triggerSwatchWrapper) {
        const swatch = this.triggerSwatchWrapper.querySelector('.swatch');
        if (swatch) {
          if (swatchValue) {
            swatch.style.setProperty('--swatch--background', swatchValue);
            swatch.classList.remove('swatch--unavailable');
          } else {
            swatch.style.setProperty('--swatch--background', 'unset');
            swatch.classList.add('swatch--unavailable');
          }
          if (swatchFocalPoint) {
            swatch.style.setProperty('--swatch-focal-point', swatchFocalPoint);
          } else {
            swatch.style.setProperty('--swatch-focal-point', 'unset');
          }
        }
      }
    }

    handleTriggerKeydown(e) {
      switch (e.key) {
        case 'Enter':
        case ' ':
        case 'ArrowDown':
          e.preventDefault();
          this.open();
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.open();
          // Focus last option
          if (this.options.length > 0) {
            this.focusedIndex = this.options.length - 1;
            this.options[this.focusedIndex].focus();
          }
          break;
      }
    }

    handleOptionKeydown(e, index) {
      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          const option = this.options[index];
          if (option.getAttribute('aria-disabled') !== 'true') {
            this.selectOption(option);
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          this.focusNextOption(index);
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.focusPreviousOption(index);
          break;
        case 'Home':
          e.preventDefault();
          this.focusedIndex = 0;
          this.options[0].focus();
          break;
        case 'End':
          e.preventDefault();
          this.focusedIndex = this.options.length - 1;
          this.options[this.focusedIndex].focus();
          break;
        case 'Tab':
          this.close();
          break;
      }
    }

    focusNextOption(currentIndex) {
      const nextIndex = (currentIndex + 1) % this.options.length;
      this.focusedIndex = nextIndex;
      this.options[nextIndex].focus();
    }

    focusPreviousOption(currentIndex) {
      const prevIndex = (currentIndex - 1 + this.options.length) % this.options.length;
      this.focusedIndex = prevIndex;
      this.options[prevIndex].focus();
    }
  }

  customElements.define('variant-dropdown', VariantDropdown);
}

